import { useEffect } from 'react';
import type { ParsedItem } from '../types';
import { UpdateCard } from './UpdateCard';

type Props = {
  item: ParsedItem | null;
  isBookmarked?: (id: string) => boolean;
  onToggleBookmark?: (item: ParsedItem) => void;
  isMuted?: (id: string) => boolean;
  onToggleMute?: (item: ParsedItem) => void;
  onClose: () => void;
};

const PANEL_WIDTH = 440;
const MOBILE_BREAKPOINT = 900;

// Right-side slide-in detail panel that replaces the old "inline card below
// the grid" pattern. Anchored to the viewport edge so the source element
// (pill, card row, stat tile) stays visible while the user inspects the
// detail. On narrow viewports it becomes a bottom sheet — same component,
// same content, different anchor.
//
// State-wise this is a pure controlled component: it renders whenever `item`
// is non-null. Parent owns the selection state so any surface in the app
// (hero, kanban, priority list, future views) can trigger it.
export function DetailPanel({
  item,
  isBookmarked,
  onToggleBookmark,
  isMuted,
  onToggleMute,
  onClose,
}: Props) {
  useEffect(() => {
    if (!item) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [item, onClose]);

  if (!item) return null;

  return (
    <>
      {/* Backdrop — click to dismiss. Kept very subtle so the surface behind
          stays readable; this isn't a true modal. */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.18)',
          zIndex: 40,
          animation: 'fadeIn 0.15s ease',
        }}
      />

      {/* Panel. Desktop: right-anchored 440px column. Mobile: full-width
          sheet from the bottom. The @media-style branch is done with JS so
          we don't have to add a new stylesheet just for this. */}
      <div
        role="dialog"
        aria-label={item.title}
        style={{
          position: 'fixed',
          background: 'var(--card-bg, #fff)',
          boxShadow: '-12px 0 32px rgba(0,0,0,0.18)',
          zIndex: 50,
          overflowY: 'auto',
          ...panelLayout(),
          animation: 'slideInRight 0.18s ease',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 18px',
            borderBottom: '1px solid var(--border, #E5E7EB)',
            position: 'sticky',
            top: 0,
            background: 'var(--card-bg, #fff)',
            zIndex: 1,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: 'var(--text-muted, #6B7280)',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Päivityksen tiedot
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Sulje"
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              border: 'none',
              background: 'var(--surface-muted, rgba(148,163,184,0.15))',
              color: 'var(--text, #1F2937)',
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 16 }}>
          <UpdateCard
            item={item}
            bookmarked={isBookmarked?.(item.id)}
            onToggleBookmark={onToggleBookmark}
            muted={isMuted?.(item.id)}
            onToggleMute={onToggleMute}
            defaultExpanded
          />
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @media (max-width: ${MOBILE_BREAKPOINT}px) {
          [role="dialog"][aria-label] {
            animation: slideInBottom 0.2s ease !important;
          }
        }
        @keyframes slideInBottom {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
      `}</style>
    </>
  );
}

function panelLayout(): React.CSSProperties {
  // SSR-safe default = desktop layout. On mount, the `window` check runs
  // during render in the browser, swapping to the bottom-sheet form when
  // the viewport is narrower than the breakpoint.
  if (typeof window === 'undefined' || window.innerWidth > MOBILE_BREAKPOINT) {
    return {
      top: 0,
      right: 0,
      bottom: 0,
      width: PANEL_WIDTH,
    };
  }
  return {
    left: 0,
    right: 0,
    bottom: 0,
    maxHeight: '85vh',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
  };
}
